'use client';

import { useCallback, useState } from 'react';

import {
	AdminAiProblemJsonButton,
	type InterviewSession as InterviewSessionData,
	startDialogAction,
	type StartDialogResult,
} from '@/features/ai-dialog';
import type { GetAIProblemResponseV1 } from '@/shared/api';

import type { BreadcrumbItem } from './breadcrumbs';
import { InterviewGate } from './interview-gate';
import { InterviewSession } from './interview-session';

type InterviewScreenProps = {
	problemId: string;
	problem: GetAIProblemResponseV1;
	languageId?: number;
	session: InterviewSessionData;
	nextHref: string;
	nextLabel: string;
	/** Соседние задачи курса для стрелок в шапке (undefined на границах курса). */
	coursePrevHref?: string;
	courseNextHref?: string;
	breadcrumbs: BreadcrumbItem[];
};

/**
 * Диспетчер интервью:
 * - `gate` → экран с сообщением + кнопкой «Начать» (создаёт диалог по клику);
 * - `ready` → рабочая сессия диалога.
 *
 * Решение о режиме принимается на сервере (см. prepareInterviewSession).
 */
export function InterviewScreen({
	problemId,
	problem,
	languageId,
	session,
	nextHref,
	nextLabel,
	coursePrevHref,
	courseNextHref,
	breadcrumbs,
}: InterviewScreenProps) {
	const [started, setStarted] = useState<StartDialogResult | null>(
		session.mode === 'ready'
			? {
					dialogId: session.dialogId,
					dialog: session.dialog,
					messages: session.messages,
				}
			: null,
	);

	// «Пройти заново» — создаём новый диалог и пересоздаём сессию.
	const handleRestart = useCallback(async () => {
		const result = await startDialogAction(problemId, languageId);
		if (result) setStarted(result);
	}, [problemId, languageId]);

	return (
		<div className="relative h-full w-full">
			<div className="absolute right-4 top-4 z-30 flex items-center gap-2">
				<AdminAiProblemJsonButton problemId={problemId} />
			</div>
			{!started ? (
				<InterviewGate
					problemId={problemId}
					problem={problem}
					languageId={languageId}
					onStarted={setStarted}
				/>
			) : (
				<InterviewSession
					problemId={problemId}
					problem={problem}
					dialogId={started.dialogId}
					initialDialog={started.dialog}
					initialMessages={started.messages}
					onRestart={handleRestart}
					nextHref={nextHref}
					nextLabel={nextLabel}
					coursePrevHref={coursePrevHref}
					courseNextHref={courseNextHref}
					breadcrumbs={breadcrumbs}
				/>
			)}
		</div>
	);
}
